import kebabCase from 'lodash.kebabcase'
import { WithAdditionalMarketoFormOverrideProps } from 'marketing-site/src/components/context/MarketoFormOverridePropsContext'
import { Form, IFormValues, IOnSubmitResponse } from 'marketing-site/src/library/elements/Form'
import { IFormField } from 'marketing-site/src/library/elements/Form/@types/FormField'
import { Text } from 'marketing-site/src/library/elements/Text'
import { AnalyticsEvent, Color, mq, useMediaQuery } from 'marketing-site/src/library/utils'
import React, { useState } from 'react'
import topSquiggle from '../../images/roi-squiggle1.svg'
import bottomSquiggle from '../../images/roi-squiggle2.svg'
import { IMarketoFormValues } from '../MarketoFormV2'
import { IProps } from './index'
import { Result } from './result'
import roiCalculator from './utils/roiCalculator'
import { useMarketoForms } from 'marketing-site/src/components/context/MarketoFormsContext'
import { captureException } from 'marketing-site/lib/sentry'
import getInitialValues from 'marketing-site/src/library/components/MarketoFormV2/utils/getInitialValues'
import { submit as submitMarketoFormV2 } from 'marketing-site/src/library/components/MarketoFormV2/utils/submission'
import useTranslate from '../../hooks/useTranslate'
import { useOptimizelyFeatureFlag } from '../../hooks/useOptimizelyFeatureFlag'
import { SEND_EMPTY_FIELDS_TO_MARKETO_FF } from 'marketing-site/lib/optimizelyExperiments'

const resultsProps = [
  ['Cost savings', 'mo', 'The total amount of cost savings per month', '$'],
  ['Hours saved', 'mo', 'The total number working hours your team saves per month'],
  [
    'Extra ticket capacity',
    'mo',
    'The total number of additional tickets your team can handle per month',
  ],
]

const submitButtonLabel = 'Calculate savings'

const formFields: IFormField[] = [
  {
    type: 'select',
    id: 'industry',
    label: 'What industry do you work in?',
    placeholder: 'Select industry',
    values: [
      { label: 'Agriculture & Food', value: 'agriculture-and-food' },
      { label: 'Automotive', value: 'automotive' },
      { label: 'Consumer Goods', value: 'consumer-goods' },
      { label: 'E-Commerce', value: 'ecommerce' },
      { label: 'Education', value: 'education' },
      { label: 'Entertainment & Gaming', value: 'entertainment-and-gaming' },
      { label: 'Facilities', value: 'facilities' },
      { label: 'Financial Services', value: 'financial-services' },
      { label: 'Government & Non Profit', value: 'government-and-non-profit' },
      { label: 'Health & Wellness', value: 'health-and-wellness' },
      { label: 'Healthcare', value: 'healthcare' },
      { label: 'Human Resources', value: 'human-resources' },
      { label: 'Media & Telecommunications', value: 'media-and-telecommunications' },
      { label: 'Professional Services', value: 'professional-services' },
      { label: 'Real Estate', value: 'real-estate' },
      { label: 'Retail', value: 'retail' },
      { label: 'Software & Technology', value: 'software-and-technology' },
      { label: 'Travel', value: 'travel' },
      { label: 'Utilities & Energy', value: 'utilities-and-energy' },
      { label: 'Other', value: 'other' },
    ],
    required: true,
  },
  {
    type: 'number',
    id: 'numberOfAgents',
    label: 'How many agents work at your company?',
    placeholder: 'Enter number of agents',
    required: true,
    min: 1,
  },
  {
    type: 'number',
    id: 'averageHourlySalary',
    label: 'What is their average hourly salary?',
    placeholder: 'Enter the average hourly salary',
    required: true,
    min: 1,
    prefix: '$',
  },
  {
    type: 'number',
    id: 'averageTickets',
    label: 'How many tickets are handled monthly?',
    placeholder: 'Enter monthly number of tickets',
    required: true,
    min: 1,
  },
  {
    type: 'number',
    id: 'minutesPerTicket',
    label: 'How many minutes does it take to close a ticket?',
    placeholder: 'Enter average time in minutes',
    required: true,
    min: 1,
  },
]

const genericErrorResponse = { success: false, errorMessage: 'Could not calculate results.' }

export function ROICalculator({ renderCTA, marketoFormId }: IProps) {
  const [results, setResults] = useState<[number, number, number]>([0, 0, 0])
  const isFormSubmitted = results.some((value) => value !== 0)
  const isComputerScreen = useMediaQuery(`(${mq.laptop})`)

  const marketoForms = useMarketoForms()
  const marketoForm = marketoFormId
    ? marketoForms.find((f) => f.id === parseInt(marketoFormId))
    : null

  const marketoFormRedirectUrl =
    'https://marketing.intercomcdn.com/assets/books/csf-starter-kit-2.pdf'

  const [marketoFormDefaults, setMarketoFormDefaults] = useState<IMarketoFormValues>({})
  const sendEmptyFormValues = useOptimizelyFeatureFlag(SEND_EMPTY_FIELDS_TO_MARKETO_FF)

  function onSubmit(values?: IFormValues): IOnSubmitResponse {
    if (!values) return genericErrorResponse

    const numberOfAgents = parseFloat(values['numberOfAgents'])
    const averageTickets = parseFloat(values['averageTickets'])
    const minutesPerTicket = parseFloat(values['minutesPerTicket'])
    const averageHourlySalary = parseFloat(values['averageHourlySalary'])
    const email = values['email']

    try {
      const results = roiCalculator(averageTickets, minutesPerTicket, averageHourlySalary)
      setResults([results.revenueSaved, results.hoursSaved, results.extraTicketsHandled])

      const newMarketoFormDefaults = {
        rOICalcSupportAgents: `${Math.round(numberOfAgents)}`,
        rOICalcTicketsperMonth: `${Math.round(averageTickets)}`,
        rOICalcTimetoClose: `${Math.round(minutesPerTicket)}`,
        rOICalcAvgAgentHourly: `${averageHourlySalary}`,
        rOICalcRevenueSavedinMonth: `${results.revenueSaved}`,
        rOICalcHoursSavedinMonth: `${results.hoursSaved}`,
        rOICalcNumAdditionalTicketsIntercom: `${results.extraTicketsHandled}`,
      }
      setMarketoFormDefaults(newMarketoFormDefaults)

      const event = new AnalyticsEvent({
        action: 'submitted',
        object: 'submit-button',
        context: 'roi-calculator-form',
        metadata: { results, values },
      })
      event.setPlaceFromPath(window.location.pathname)
      event.send()

      if (marketoForm) {
        const initialValues = getInitialValues(marketoForm.fields, sendEmptyFormValues)
        submitMarketoFormV2(
          marketoForm.id,
          {
            ...initialValues,
            ...newMarketoFormDefaults,
            Email: email,
          },
          'roi-calculator-submission',
        )
      } else if (marketoFormId) {
        captureException(
          new Error(
            `ROICalculator tried to load a non-existent marketo form with id=${marketoFormId}`,
          ),
        )
      }

      return { success: true }
    } catch (err) {
      if (err instanceof Error) return { success: false, errorMessage: err.message }

      return genericErrorResponse
    }
  }

  const { translate } = useTranslate()

  return (
    <>
      <div className="roi-calculator-container">
        <div className="roi-calculator">
          <div className="form">
            <div className="form-header">
              <Text size="heading-3" brand="default">
                {translate("Estimate the time and money you'll save each month")}
              </Text>
            </div>
            <Form
              fields={[
                ...formFields,
                ...(!!marketoForm
                  ? [
                      {
                        type: 'email',
                        id: 'email',
                        label: 'What is your work email?',
                        placeholder: 'Enter your work email',
                        required: true,
                        forceBusinessEmail: true,
                      } as IFormField,
                    ]
                  : []),
              ]}
              submitButtonLabel={submitButtonLabel}
              onSubmit={onSubmit}
              showResetButton={true}
            />
          </div>

          <div className="results-section">
            <div className="results">
              {resultsProps.map(([item, timeUnit, tooltipText, currency], index) => (
                <Result
                  caption={translate(item)}
                  timeUnit={translate(timeUnit)}
                  currency={currency}
                  isFormSubmitted={isFormSubmitted}
                  resultNumber={results[index]}
                  tooltipText={translate(tooltipText)}
                  isComputerScreen={isComputerScreen}
                  key={kebabCase(item)}
                />
              ))}
            </div>

            <div className="disclaimer">
              {isFormSubmitted && (
                <Text size="caption" brand="default">
                  {translate(
                    "This calculation is based on the average results seen by customers who are actively using Intercom's products.",
                  )}
                </Text>
              )}
            </div>

            <div className="cta-section">
              <div className="eyebrow">
                <Text size="eyebrows" brand="default">
                  {translate('Next steps')}
                </Text>
              </div>

              <div className="cta">
                <Text size="body" brand="default">
                  {translate('Learn more about how Conversational Support can work for your team.')}
                </Text>
              </div>
              <div className="button">
                <WithAdditionalMarketoFormOverrideProps
                  overrideProps={{
                    defaults: marketoFormDefaults,
                    redirectUrl: marketoFormRedirectUrl,
                  }}
                >
                  {renderCTA()}
                </WithAdditionalMarketoFormOverrideProps>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>
        {`
          .form {
            background-color: ${Color.Sky};
            padding: 30px 40px 32px 7%;

            .form-header {
              margin-bottom: 30px;
              max-width: 575px;
            }

            :global(.form-element-wrapper) {
              padding-bottom: 50px;
            }

            :global(.form-element-wrapper .form-field .number-input),
            :global(.form-element-wrapper .form-field select),
            :global(.form-element-wrapper .form-field input[type='email']) {
              border-radius: 30px !important;
            }

            :global(.form-element-wrapper .form-field label) {
              font-size: 16px;
              margin-top: 10px;
            }
          }

          .results-section {
            padding: 72px 40px 32px 7%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            .disclaimer {
              opacity: 50%;
              min-height: 38px;
            }

            .cta-section {
              margin-bottom: 20px;

              .eyebrow {
                text-transform: uppercase;
                display: block;
                margin-top: 70px;
                margin-bottom: 10px;
              }

              .cta {
                margin-bottom: 37px;
              }

              .button {
                width: 320px;
              }
            }
          }

          @media (${mq.laptop}) {
            .top-squiggle {
              position: absolute;
              width: 360px;
              height: 406px;
              background: url(${topSquiggle});
              z-index: -1;
            }
            .roi-calculator {
              display: grid;
              grid-template-columns: 1fr 1.5fr;
              margin: 25px 30px 0;
              border-radius: 20px;
              box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
              background: ${Color.White};

              .form {
                border-radius: 20px 0 0 20px;
                padding-top: 32px;
                padding-left: 29px;

                .form-header {
                  margin-bottom: 40px;

                  :global(span) {
                    font-weight: bold;
                  }
                }
              }

              .results-section {
                padding: 90px 35px 60px 50px;
                border-top: 1px solid ${Color.Stone};
                border-top-right-radius: 20px;

                .disclaimer {
                  max-width: 400px;
                  margin-top: 22px;
                }

                .cta-section {
                  .cta {
                    margin-bottom: 24px;
                    max-width: 433px;
                  }
                }
              }
            }
            .bottom-squiggle {
              position: absolute;
              right: 0;
              width: 414px;
              height: 453px;
              background: url(${bottomSquiggle});
              z-index: -1;
              transform: translateY(-360px);
            }
          }

          @media (${mq.desktop}) {
            .roi-calculator {
              margin-left: 70px;
              margin-right: 70px;
            }
          }

          @media (${mq.desktopLg}) {
            .roi-calculator {
              margin-left: auto;
              margin-right: auto;
              max-width: 1220px;

              .results-section {
                padding: 120px 55px 60px 70px;
              }
            }
          }
        `}
      </style>
    </>
  )
}
